import React, { useState, useEffect, useRef } from "react";
import Spinner from "../../components/Tools/spinner";
import { useSelector } from 'react-redux';
import { BankSettlementBeneficiaries } from "./../../helper/Api";
import DataTable from 'react-data-table-component';
import SubHeader from "../../components/Subheader/Subheader";
import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import moment from "moment";
import MainFilter from "../../components/Tools/MainFilter";
import './BankSettlementOnHold.css';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 64;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_CARD_PADDING = 16;
const TABLE_BOTTOM_GAP = 24;

const buildUniqueRowKey = (row, index) => [
    row.id,
    row.transaction_reference,
    row.beneficiary_account_no,
    row.created_at,
    index,
].join('-');

const BankSettlementBeneficiariesConsolidated = () => {
    const { financialYears, authData } = useSelector((state) => state.authData);
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const tableContainerRef = useRef(null);
    const fyList = financialYears.payload.fy_list;
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [bankMailRecords, setBankMailRecords] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');
    const [filterPayload, setFilterPayload] = useState({
          searchStr: "",
          fromDate: "",
          toDate: "",
          dateColumn: "",
          department_id: "",
          tier: ""
      })
    const customStyles = {
        headCells: {
        style: {
            fontWeight: "bold",
            fontSize: "14px"
        },
        },
    };
    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
    }

    const DateDifference = (createdAt) => {
            const givenDate = moment(createdAt, "YYYY-MM-DD HH:mm:ss"); 
            const today = moment();
            const diffInDays = today.diff(givenDate, "days");
            return diffInDays;
        }

    const getPaymentStatus = (row) => {
        if (row.flag_success_return === "1") {
            return { label: 'Success', tone: 'success' };
        }
        if (row.flag_success_return === "3") {
            return { label: 'On Hold', tone: 'hold' };
        }
        if (row.flag_success_return === "0" || row.flag_success_return === "2") {
            return { label: 'Failed', tone: 'danger' };
        }
        if (row.reason_code !== "" && row.reason_code !== "00") {
            return { label: 'Failed', tone: 'danger' };
        }
        return { label: 'Pending', tone: 'warning' };
    };

    const isDaysApplicable = (status) => status.tone === 'warning' || status.tone === 'hold';

    const getDaysBadgeTone = (days) => {
        if (days <= 4) return 'primary';
        if (days <= 6) return 'warning';
        return 'danger';
    };

    const columns = [
        {
            name: 'Sett. Id',
            selector: (row) => row.id,
            width: "100px"
        },
        {
            name: '+ Days',
            cell: (row) => {
                const status = getPaymentStatus(row);
                if (!isDaysApplicable(status)) {
                    return <span className="bank-settlement-days-na">NA</span>;
                }
                const days = DateDifference(row.created_at);
                const badgeTone = getDaysBadgeTone(days);
                return (
                    <span className={`bank-settlement-days-badge bank-settlement-days-badge--${badgeTone}`}>
                        {days}+
                    </span>
                );
            },
            center: true,
            width: "90px",
        },
        {
            name: 'Status',
            cell: (row) => {
                const status = getPaymentStatus(row);
                return (
                    <div className="bank-settlement-status-cell">
                        <span className={`bank-settlement-status-cell__label bank-settlement-status-cell__label--${status.tone}`}>
                            {status.label}
                        </span>
                        {status.tone !== 'warning' && row.reason_code ? (
                            <span className={`bank-settlement-status-cell__badge bank-settlement-status-cell__badge--${status.tone}`}>
                                {row.reason_code}
                            </span>
                        ) : null}
                    </div>
                );
            },
            center: true,
            width: "110px",
        },
        {
            name: 'Tran. Ref',
            selector: (row) => row.transaction_reference,
            width: "200px"
        },
        {
            name: 'ACH / Product',
            cell: (row) => (
                <div className="bank-settlement-bank-cell">
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">ACH Code:</span>
                        <span className="bank-settlement-bank-cell__value">{row.ach_transaction_code || '-'}</span>
                    </div>
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">Product Type:</span>
                        <span className="bank-settlement-bank-cell__value">{row.product_type || '-'}</span>
                    </div>
                </div>
            ),
            wrap: true,
            minWidth: "160px",
        },
        {
            name: 'Bene. Name',
            selector: (row) => row.beneficiary_name,
            sortable: true,
            wrap: true
        },
        {
            name: 'User Name',
            selector: (row) => row.user_name,
            sortable: true,
            wrap: true
        },
        {
            name: 'Amount',
            selector: (row) => Number(row.amount_paise) / 100,
            sortable: true,
            wrap: true
        },
        {
            name: 'Beneficiary Bank',
            cell: (row) => (
                <div className="bank-settlement-bank-cell">
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">IFSC:</span>
                        <span className="bank-settlement-bank-cell__value">{row.destination_ifsc_micr_iin || '-'}</span>
                    </div>
                    <div className="bank-settlement-bank-cell__row bank-settlement-bank-cell__row--account">
                        <span className="bank-settlement-bank-cell__label">A/C:</span>
                        <span className="bank-settlement-bank-cell__value bank-settlement-bank-cell__value--account">
                            {row.beneficiary_account_no || '-'}
                        </span>
                    </div>
                </div>
            ),
            wrap: true,
            minWidth: "200px",
        },
        {
            name: 'Sponsor IFSC',
            cell: (row) => (
                <span className="bank-settlement-sponsor-ifsc">
                    {row.sponsor_ifsc_micr_iin || '-'}
                </span>
            ),
            wrap: true,
            minWidth: "130px",
        },
        {
            name: 'User Number',
            selector: (row) => row.user_number,
            sortable: true,
            wrap: true
        },
        {
            name: 'Created At',
            selector: (row) => row.created_at,
            sortable: true,
            wrap: true,
        }
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
    };

    const fetchBankSettlementBeneficiaries = async () => {
        try {
            setLoading(true);
            const res = await BankSettlementBeneficiaries({});
            if (res.data.status) {
                setBankMailRecords(
                    res.data.data.map((row, index) => ({
                        ...row,
                        rowKey: buildUniqueRowKey(row, index),
                    }))
                );
            }
        } catch (error) {
            console.log("fetchBankSettlementBeneficiaries error", error);
            setBankMailRecords([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchBankSettlementBeneficiaries();
    }, []);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.max(bankMailRecords.length, 1);
            const visibleRows = Math.min(rowCount, rowsPerPage);
            const bodyContentHeight = visibleRows * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT + 16;
            const containerTop = tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight = window.innerHeight
                - containerTop
                - TABLE_PAGINATION_HEIGHT
                - TABLE_CARD_PADDING
                - TABLE_BOTTOM_GAP;
            const viewportBody = Math.max(maxBodyHeight, 200);
            setTableScrollHeight(`${Math.min(bodyContentHeight, viewportBody)}px`);
        };

        const raf = window.requestAnimationFrame(updateTableHeight);
        window.addEventListener('resize', updateTableHeight);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', updateTableHeight);
        };
    }, [bankMailRecords.length, rowsPerPage, loading]);

    // A short fixedHeader box hides the only row under the sticky header.
    const useFixedHeader = bankMailRecords.length > 5;

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleInput = async (e) => {
        const {name, value} = e.target;
        setFilterPayload((prev) => ({
            ...prev, [name]: value
        }));
    }
    const handleFilterRemove = async (name) => {
        const newPayload = {
        ...filterPayload,
        [name]: "",
        };
        setFilterPayload(newPayload);
    }
    const handleFilterClick = async () => {
        setOpen(false);
        // await loadFullDashboard(fyId);
    };

    return (
        <div className="bank-settlement-table-page">
            {loading && <div><Spinner /></div>}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger}/>
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin:0}}>Bank Settlement Beneficiaries Consolidated</h4>
                    <CustomBreadcrumbs linksArray={[]} title="Bank Settlement Beneficiaries Consolidated"/>
                </div>
                <div className="col-md-6">
                    
                </div>
            </div>
            <div ref={tableContainerRef} className="bank-settlement-table-card">
                <DataTable
                    customStyles={customStyles}
                    className="bank-settlement-data-table"
                    columns={columns}
                    data={bankMailRecords}
                    keyField="rowKey"
                    persistTableHead
                    fixedHeader={useFixedHeader}
                    fixedHeaderScrollHeight={useFixedHeader ? tableScrollHeight : undefined}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={bankMailRecords.length}
                    paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>
            <MainFilter 
                open={open} 
                handleClose={handleClose} 
                handleInput={handleInput} 
                handleOpen={handleOpen}
                filterPayload={filterPayload}
                handleFilterClick={handleFilterClick}
            />
        </div>
    )
}

export default BankSettlementBeneficiariesConsolidated;